import { getLocale } from "./locale";

/**
 * A moeda dos valores da Tãmi: real, sempre. O idioma muda a formatação
 * (separadores, posição e grafia do símbolo), nunca a moeda — um cachê de
 * R$ 1.000 não vira US$ 1.000 porque a tela está em inglês.
 */
const CURRENCY = "BRL";

/**
 * Moeda em real, no formato do idioma de agora. Morava em
 * `apps/web/src/lib/utils.ts`, e o mobile repetia a mesma chamada com outro
 * nome (`formatCurrencyBRL`, em `features/dashboard/lib/format-metrics.ts`) —
 * mesma fórmula, dois nomes.
 *
 * `Intl.NumberFormat` fica dentro da função (regra do pacote): criá-lo no
 * escopo do módulo congelaria o locale da máquina que carregou o módulo.
 */
export function formatCurrency(value: number): string {
  return currencyFormat().format(value);
}

/**
 * O símbolo do real como o idioma de agora o escreve ("R$" nos três), para
 * texto que cita a moeda sem um valor — "Valor (R$)". Sai da mesma
 * formatação de `formatCurrency`, para os dois nunca divergirem.
 */
export function currencySymbol(): string {
  return currencySymbolOf(currencyFormat());
}

/**
 * O símbolo de um formatador de moeda. O Hermes do app (iOS) não tem
 * `formatToParts` no `NumberFormat` — chamar direto quebrava a tela de
 * Campanhas com "undefined is not a function". Sem ele, o símbolo é o que
 * sobra do zero formatado depois de tirar dígitos, separadores e espaços.
 */
export function currencySymbolOf(
  format: Pick<Intl.NumberFormat, "format"> & Partial<Pick<Intl.NumberFormat, "formatToParts">>,
): string {
  if (typeof format.formatToParts === "function") {
    const parts = format.formatToParts(0);
    return parts.find((part) => part.type === "currency")?.value ?? CURRENCY;
  }
  const symbol = format.format(0).replace(/[\d.,\s\u00a0\u202f-]/g, "");
  return symbol || CURRENCY;
}

/**
 * `narrowSymbol` escreve "R$" também no es-ES, onde o padrão do Intl é o
 * código ("1234,50 BRL"). Motor sem suporte à opção lança `RangeError`: cai
 * no símbolo padrão do idioma.
 */
function currencyFormat(): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: CURRENCY,
      currencyDisplay: "narrowSymbol",
    });
  } catch {
    return new Intl.NumberFormat(getLocale(), { style: "currency", currency: CURRENCY });
  }
}
